import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Hammer, CheckCircle2, Plus, History, Search } from 'lucide-react';
import { toast } from 'sonner';

const ReworkTasksPage = () => {
  const { api } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('open');
  const [createOpen, setCreateOpen] = useState(false);
  const [historyFor, setHistoryFor] = useState(null); // serial number
  const [historyRows, setHistoryRows] = useState([]);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    serial_number: '', item_sku: '', item_name: '', work_order_id: '',
    rework_notes: '', severity: 'normal',
  });

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const r = await api().get('/rework-tasks');
      setTasks(r.data || []);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchTasks(); }, []);

  const filtered = useMemo(() => {
    let rows = tasks.filter(t => t.status === tab);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(t =>
        (t.item_sku || '').toLowerCase().includes(q)
        || (t.item_name || '').toLowerCase().includes(q)
        || (t.serial_number || '').toLowerCase().includes(q)
        || (t.rework_notes || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [tasks, tab, search]);

  const counts = useMemo(() => ({
    open: tasks.filter(t => t.status === 'open').length,
    completed: tasks.filter(t => t.status === 'completed').length,
  }), [tasks]);

  const complete = async (t) => {
    try {
      await api().post(`/rework-tasks/${t.id}/complete`);
      toast.success('Marked complete');
      fetchTasks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.rework_notes.trim()) { toast.error('Notes required'); return; }
    if (!form.serial_number && !form.item_sku && !form.work_order_id) {
      toast.error('Enter a serial number, SKU, or Work Order ID'); return;
    }
    try {
      await api().post('/rework-tasks', form);
      toast.success('Rework task created');
      setCreateOpen(false);
      setForm({ serial_number: '', item_sku: '', item_name: '', work_order_id: '', rework_notes: '', severity: 'normal' });
      fetchTasks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create rework task');
    }
  };

  const showHistory = async (serial) => {
    if (!serial) return;
    try {
      const r = await api().get(`/rework-tasks/history-by-serial/${encodeURIComponent(serial)}`);
      setHistoryRows(r.data || []);
      setHistoryFor(serial);
    } catch (e) {
      toast.error('Failed to load history');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-slideIn" data-testid="rework-tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Rework Queue</h1>
          <p className="text-slate-500 text-sm">QC failures + manually-flagged units that need to be reworked. Each task is linked back to its original quality check, work order, and (for finished units) serial number.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="rounded-sm" data-testid="rework-send-btn">
          <Plus className="w-4 h-4 mr-2" /> Send Unit to Rework
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="open" data-testid="rework-tab-open">Open ({counts.open})</TabsTrigger>
          <TabsTrigger value="completed" data-testid="rework-tab-completed">Completed ({counts.completed})</TabsTrigger>
        </TabsList>

        <div className="flex items-center gap-2 mt-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input className="pl-8 rounded-sm" placeholder="Search SKU / serial / notes…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="rework-search" />
          </div>
        </div>

        <TabsContent value={tab} className="mt-4">
          <Card className="industrial-card">
            <CardContent className="p-0">
              {filtered.length === 0 ? (
                <div className="p-12 text-center text-slate-500"><Hammer className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>No rework tasks</p></div>
              ) : (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Serial / Unit</TableHead>
                      <TableHead>WO</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(t => (
                      <TableRow key={t.id} data-testid={`rework-row-${t.id}`}>
                        <TableCell><span className="mono">{t.item_sku || '—'}</span> · {t.item_name || ''}</TableCell>
                        <TableCell className="text-xs mono">{t.serial_number || '—'}</TableCell>
                        <TableCell className="text-xs text-slate-500 mono">{t.work_order_id?.slice(0, 8) || '—'}</TableCell>
                        <TableCell><span className={`text-[10px] px-2 py-0.5 rounded ${t.source === 'manual' ? 'bg-amber-50 border border-amber-300 text-amber-700' : 'bg-blue-50 border border-blue-300 text-blue-700'}`}>{t.source || 'auto'}</span></TableCell>
                        <TableCell><span className={`text-[10px] px-2 py-0.5 rounded ${t.severity === 'critical' ? 'bg-red-50 border border-red-300 text-red-700' : t.severity === 'urgent' ? 'bg-orange-50 border border-orange-300 text-orange-700' : 'bg-slate-50 border border-slate-300 text-slate-700'}`}>{t.severity || 'normal'}</span></TableCell>
                        <TableCell className="text-sm max-w-[280px] truncate text-slate-600">{t.rework_notes}</TableCell>
                        <TableCell className="text-xs text-slate-500">{new Date(t.created_at).toLocaleDateString()}<br /><span className="text-[10px]">{t.created_by_name}</span></TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {t.serial_number && (
                              <Button size="sm" variant="ghost" onClick={() => showHistory(t.serial_number)} title="Rework history" data-testid={`rework-history-${t.id}`}>
                                <History className="w-4 h-4" />
                              </Button>
                            )}
                            {t.status === 'open' ? (
                              <Button size="sm" onClick={() => complete(t)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`rework-complete-${t.id}`}>
                                <CheckCircle2 className="w-4 h-4 mr-1" /> Mark Complete
                              </Button>
                            ) : (
                              <span className="text-xs text-slate-500">{t.completed_at ? new Date(t.completed_at).toLocaleDateString() : ''} · {t.completed_by_name}</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Send-to-rework dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-xl" data-testid="rework-create-dialog">
          <DialogHeader>
            <DialogTitle>Send Unit to Rework</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2 text-blue-800">
              Use this when a previously-passed unit needs rework (field issue, customer return, late defect found). The task will appear in the queue with source = manual.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Serial Number</Label>
                <Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value.toUpperCase() })} placeholder="SN-2026-…" className="rounded-sm mono" data-testid="rework-serial-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="rework-severity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Item SKU (optional)</Label>
                <Input value={form.item_sku} onChange={(e) => setForm({ ...form, item_sku: e.target.value.toUpperCase() })} placeholder="optional" className="rounded-sm mono" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Work Order ID (optional)</Label>
                <Input value={form.work_order_id} onChange={(e) => setForm({ ...form, work_order_id: e.target.value })} placeholder="optional" className="rounded-sm mono" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Rework Notes *</Label>
              <Textarea value={form.rework_notes} onChange={(e) => setForm({ ...form, rework_notes: e.target.value })} rows={4} placeholder="What's wrong? What needs to happen?" className="rounded-sm" data-testid="rework-notes-input" required />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" className="bg-amber-600 hover:bg-amber-700 rounded-sm" data-testid="rework-create-submit">
                <Hammer className="w-4 h-4 mr-2" /> Create Rework Task
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rework history dialog */}
      <Dialog open={!!historyFor} onOpenChange={(v) => !v && setHistoryFor(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="rework-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-4 h-4" /> Rework history · <span className="mono text-base">{historyFor}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {historyRows.length === 0 && <div className="text-sm text-slate-500 text-center py-8">No rework events found.</div>}
            {historyRows.map(r => (
              <div key={r.id} className={`p-3 rounded border ${r.status === 'completed' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex justify-between items-start text-xs mb-1">
                  <span className="font-semibold">{r.source === 'manual' ? 'Manual' : 'QC Auto'} · {r.severity || 'normal'}</span>
                  <span className="text-slate-500">{new Date(r.created_at).toLocaleString()}</span>
                </div>
                <div className="text-sm text-slate-700">{r.rework_notes}</div>
                <div className="text-xs text-slate-500 mt-1">By {r.created_by_name} · status <strong>{r.status}</strong>{r.completed_at ? ` · completed ${new Date(r.completed_at).toLocaleString()} by ${r.completed_by_name || ''}` : ''}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReworkTasksPage;
